import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Youtube, Sparkles, Clock, ChartNoAxesColumn, Globe, BadgeCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import pathsData from "@/data/publicPaths.json";

export default function PublicPath() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const path = pathsData.paths.find((p) => p.slug === slug);

  useEffect(() => {
    if (path) {
      document.title = `${path.title} — Free Learning Path | AI Pathshaalaa`;
    }
    window.scrollTo(0, 0);
  }, [path]);

  if (!path) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 pt-40 pb-24 text-center">
          <h1 className="font-heading font-bold text-3xl">Path not found</h1>
          <Link to="/paths" className="mt-4 inline-block text-primary font-semibold hover:underline">Browse all learning paths →</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-path-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <nav className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            <Link to="/paths" className="hover:text-primary transition-colors">Learning paths</Link>
            <span className="mx-2">/</span>
            <span className="text-primary">{path.title}</span>
          </nav>
          <h1 className="mt-4 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl">{path.title}</h1>
          <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl">{path.tagline}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {[
              { icon: ChartNoAxesColumn, label: path.level },
              { icon: Clock, label: path.duration },
              { icon: Globe, label: "English · Hindi · Hinglish" },
              { icon: BadgeCheck, label: "100% free videos" },
            ].map((b) => (
              <span key={b.label} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border bg-card text-muted-foreground">
                <b.icon className="h-3.5 w-3.5 text-primary" /> {b.label}
              </span>
            ))}
          </div>
        </motion.div>

        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="mt-10">
          <h2 className="font-heading font-bold text-xl md:text-2xl">What you'll learn</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{path.description}</p>
          <ol className="mt-6 space-y-3" data-testid="public-path-modules">
            {path.modules.map((m, i) => (
              <li key={m} className="flex items-center gap-4 rounded-2xl border border-border bg-card px-5 py-4">
                <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary font-heading font-bold text-sm shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-semibold">{m}</span>
                <Check className="h-4 w-4 text-emerald-600 ml-auto shrink-0" />
              </li>
            ))}
          </ol>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}
          className="mt-10 rounded-3xl border border-primary/25 bg-card p-7 md:p-8 relative overflow-hidden beam-top">
          <span className="orb h-56 w-56 bg-primary/40 -top-16 -right-10" />
          <h2 className="relative font-heading font-bold text-2xl">This is the generic version.<br />Yours will be personal.</h2>
          <p className="relative mt-2 text-sm text-muted-foreground max-w-xl">
            Take the 2-minute quiz and AI Pathshaalaa rebuilds this path around <em>your</em> level, profession, goal and language —
            with the best free YouTube lessons for each step, refreshed daily.
          </p>
          <div className="relative mt-6 flex items-center gap-4 flex-wrap">
            <Button size="lg" data-testid="public-path-cta" onClick={() => navigate("/quiz")} className="rounded-full px-7">
              Build my personalized path — free <ArrowRight className="h-4 w-4" />
            </Button>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Youtube className="h-4 w-4 text-red-500" /> Videos play in the official YouTube player
            </span>
          </div>
        </motion.section>

        <div className="mt-10">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">More paths</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {pathsData.paths.filter((p) => p.slug !== slug).slice(0, 5).map((p) => (
              <Link key={p.slug} to={`/paths/${p.slug}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-border bg-card hover:border-primary/40 hover:text-primary transition-colors">
                <Sparkles className="h-3.5 w-3.5 text-primary" /> {p.title}
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
